<template>
  <div class="position-relative">
    <DropDownAbsolute :value="showDropDown" @input="$event ? openDropDown() : hideDropDown()">
      <div class="box pl-3">
        <div class="pt-1" v-if="label">{{ label }}</div>
        <div class="hvh-flex align-center space-between pt-1 pr-4">
          <DPDMultiSelectInput
            class="col-12 p-0"
            :value="showDropDown ? searchWord : ''"
            :selectedItems="value"
            :placeholder="placeholder"
            @input="openDropDown(), searchWordChanged($event)"
            @item-clicked="itemClicked"
            @click="openDropDown"
            @paste="searchPasted"
            @remove="removeItem"
            @focus="openDropDown"
            @keydown.esc="escape"
          />

          <v-icon class="mx-1" color="#666adf" small @click="$emit('input', [])" v-if="(value || []).any()">mdi-close</v-icon>
          <div @click.stop v-else>
            <v-icon @click="openDropDown" v-if="!showDropDown">mdi-menu-down</v-icon>
            <v-icon @click="hideDropDown" v-else>mdi-menu-up</v-icon>
          </div>
        </div>
      </div>
      <template #drop-down>
        <div class="py-2">
          <TreeSelect :value="value" :searchWord="searchWord" :data="data" @open="loadChildNodes" @input="itemSelected" ref="treeSelect" v-if="data">
            <template slot-scope="{ item, on }">
              <template v-if="item !== data">
                <CheckBox class="clickable mr-2" :value="isSelected(item)" @input="itemSelected(item)" v-if="item.item.type && item.item.id" />

                <DPDLeafCohort class="clickable" :item="item.item" v-on="on" v-if="item.item.type === 'cohort'" />
                <LimitLeaf v-else-if="item.item.type === 'limit-100-leaf'" />
                <DPDLeaf :class="{ clickable: item.item.type }" :item="item.item" :searchWord="searchWord" v-on="on" v-else />
                <!-- <LimitLeaf class="inline-block" v-if="shouldDrawLimit(item)" /> -->
              </template>
            </template>
          </TreeSelect>
          <div class="p-2" v-else>
            <LoadingLeaf v-if="isLoading" />
            <div v-else>
              <div class="vue-treeselect__icon-container pl-1"><span class="vue-treeselect__icon-warning"></span></div>
              <span class="vue-treeselect__tip-text vue-treeselect__no-results-tip-text">
                <template v-if="shouldNotSearchMessage">{{ shouldNotSearchMessage }}</template>
                <template v-else-if="searchWord">No results found...</template>
              </span>
            </div>
          </div>
        </div>
      </template>
    </DropDownAbsolute>
  </div>
</template>

<script>
import axios from '../../../../axios';
import eqNode from 'eqnode';
import { Debouncer, errorDebug, isObject } from '../../../../libraries/common';
import { getCriteriaCode } from '../../../../libraries/criteria';
import { loadChildNodes, pasteSplitted, createSearchId, createItemSearchId, createRootData, filterCohorts, findDPDItem } from '../../../../libraries/dpdSearch';

import TreeSelect from '../../treeselect/TreeSelect.vue';
import LoadingLeaf from '../../treeselect/LoadingLeaf.vue';
import DPDLeaf from './DPDLeaf.vue';
import DPDLeafCohort from './DPDLeafCohort.vue';
import DPDMultiSelectInput from './DPDMultiSelectInput.vue';
import CheckBox from '../../form/checkbox/CheckBox.vue';
import DropDownAbsolute from '../../dropDown/DropDownAbsolute.vue';
import LimitLeaf from './LimitLeaf.vue';

export default {
  components: { TreeSelect, LoadingLeaf, DPDLeaf, DPDLeafCohort, DPDMultiSelectInput, CheckBox, DropDownAbsolute, LimitLeaf },
  props: ['value', 'title', 'label', 'placeholder', 'categorySettings', 'cohorts'],
  data() {
    return {
      showDropDown: false,
      searchWord: undefined,
      data: null,
      isLoading: false,
      cancelRequest: null,
      searchDebouncer: new Debouncer(100),
    };
  },
  computed: {
    shouldNotSearchMessage() {
      const isCodeString = this.searchWord?.match(/^[A-z]{3}(-|\d)/i);
      if (isCodeString && (this.searchWord || '').length < 8) {
        return "Type at least 8 characters to search by code";
      } else if ((this.searchWord || '').length < 4) {
        return "Type at least 4 characters to search by keyword";
      }
      return '';
    },
    shouldSearch() {
      return !this.shouldNotSearchMessage;
    },
  },
  methods: {
    openDropDown() {
      this.showDropDown = true;
    },
    hideDropDown() {
      this.searchWord = null;
      if (this.cancelRequest)
        this.cancelRequest();
      this.data = null;
      this.showDropDown = false;
    },
    // shouldDrawLimit(item) {
    //   return item.getParent && item.getParent().children.length === 100 && item.getParent().children.last() === item;
    // },
    searchWordChanged(event) {
      this.searchWord = event;
      this.data = null;
      this.isLoading = this.shouldSearch;
      this.searchDebouncer.clear()
        .push(() => {
          if (this.cancelRequest)
            this.cancelRequest();

          this.data = null;
          if (this.shouldSearch) {
            this.isLoading = true;
            axios.get('/Analytics/Cohort/CriteriaSearch', {
              params: { query: this.searchWord, ...this.categorySettings },
              cancelToken: new axios.CancelToken(c => this.cancelRequest = c)
            })
              .then(({ data }) => data)
              .catch(error => {
                if (!axios.isCancel(error)) {
                  this.isLoading = false;
                }
              })
              .then(data => {
                var rootData = createRootData({
                  ...data,
                  Cohorts: filterCohorts(this.cohorts, this.searchWord),
                });
                this.data = rootData.any() && eqNode.fromObject({ children: rootData })
                  .forEach(f => f.item.search = f.item.search || createSearchId(f));

                if (this.data) {
                  var openSingleResult = item => {
                    this.$nextTick(() => {
                      this.$refs.treeSelect.setStateOpen({ item, open: true });
                      this.$refs.treeSelect.treeStateZoomItem(item);

                      if (item.type !== 'cohort')
                        this.loadChildNodes(item)
                          .then(() => {
                            if (item.children.length === 1)
                              openSingleResult(item.children[0]);
                          });
                    })
                  }
                  var withIdChildren = this.data.toArray().filter(f => f.id);
                  if (withIdChildren?.length === 1) {
                    openSingleResult(this.data.find(f => f.item === withIdChildren[0]))
                  }
                }
              })
              .catch(errorDebug)
              .then(() => this.isLoading = false);
          }
        });
    },
    searchPasted(event) {
      if (isObject(event)) {
        var hasAny = findDPDItem(this.value, event.search);
        if (hasAny) {
          this.searchWordChanged(getCriteriaCode(event.label));
        } else {
          this.searchWord = null;
          this.$emit('input', [...this.value, event]);
        }
      } else {
        var splitted = event.split(',')
          .map(f => f.trim())
          .filter(f => f);
        if (splitted.length === 1) {
          this.openDropDown();
          this.searchWordChanged(splitted[0]);
        } else if (splitted.length) {
          this.searchWord = null;
          this.hideDropDown();
          this.$notify('Criteria pasted!');

          this.isLoading = true;

          var requests = pasteSplitted(splitted, this.categorySettings);
          var emitItem = ([item, ...restOfItems]) => this.$nextTick(() => {
            var newItem = { ...item, search: item.search || createItemSearchId(item) };

            var hasAny = findDPDItem(this.value, newItem.search);
            if (!hasAny)
              this.$emit('input', [...this.value, newItem]);
            if (restOfItems.length)
              emitItem(restOfItems);
          });

          Promise.all(requests)
            .then(all => emitItem(all.filter(f => f)))
            .then(() => this.isLoading = false);
        }
      }
    },
    itemClicked(item) {
      this.openDropDown();
      var text = item.type !== 'bad-code' ? getCriteriaCode(item.label) || item.label : item.label;
      this.searchWordChanged(text);
    },
    loadChildNodes(item) {
      return loadChildNodes(item, this.searchWord);
    },
    itemSelected(item) {
      if (item.item.type) {
        var find = findDPDItem(this.value, item.item.search);
        this.$emit('input', (this.value || []).toggle(find || item.item));
      }
    },
    removeItem(item) {
      this.$emit('input', this.value.toggle(item));
    },
    isSelected(item) {
      return findDPDItem(this.value, item.item.search);
    },
    escape() {
      if (this.showDropDown) {
        if (this.searchWord) {
          this.searchWord = null;
          this.data = null;
        } else {
          this.hideDropDown();
        }
      }
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisDPDSearchMulti'] = this;
  },
}
</script>

<style lang="scss" scoped>
.box {
  position: relative;
  // padding: 5px 16px;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 4px 1px rgba(6, 10, 36, 0.1), 0 5px 10px 0 rgba(6, 10, 36, 0.08);
}
</style>